import { useState, useEffect } from 'react';
import { X, ChevronDown } from 'lucide-react';
import { PERMISSION_GROUPS } from '../constants/permissionGroups';

export function RoleFormModal({ isOpen, onClose, onSubmit, role = null }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selected, setSelected] = useState([]);
  const [expandedSections, setExpandedSections] = useState({});

  const mode = role ? 'edit' : 'create';

  useEffect(() => {
    if (isOpen) {
      if (role) {
        setName(role.name || '');
        setDescription(role.description || '');
        setSelected(Array.isArray(role.permissions) ? role.permissions : []);
      } else {
        setName('');
        setDescription('');
        setSelected([]);
      }
      setExpandedSections({});
    }
  }, [isOpen, role]);

  const toggle = (key) => {
    setSelected((prev) => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const selectAllInSection = (section) => {
    const keys = section.permissions.map(p => p.key);
    const allSelected = keys.every(k => selected.includes(k));
    if (allSelected) {
      setSelected(prev => prev.filter(k => !keys.includes(k)));
    } else {
      setSelected(prev => [...new Set([...prev, ...keys])]);
    }
  };

  const allPermissions = PERMISSION_GROUPS.flatMap(g => g.permissions.map(p => p.key));
  const allSelected = allPermissions.every(k => selected.includes(k));

  const selectAll = () => {
    setSelected(allSelected ? [] : allPermissions);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...(role ? { id: role._id } : {}),
      name,
      description,
      permissions: selected,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 overflow-y-auto h-full w-full flex justify-center items-center z-50 p-4">
      <div className="relative bg-white w-full max-w-lg mx-auto rounded-xl shadow-2xl border border-gray-200">
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {mode === 'create' ? 'Create New Role' : 'Edit Role'}
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              {mode === 'create' ? 'Define permissions for this role' : 'Modify permissions for this role'}
            </p>
          </div>
          <button className="text-gray-400 hover:text-gray-600 transition-colors" onClick={onClose} type="button">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-3">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1" htmlFor="roleName">
                Role Name *
              </label>
              <input
                id="roleName"
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                placeholder="e.g., Content Moderator"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1" htmlFor="roleDescription">
                Description
              </label>
              <input
                id="roleDescription"
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                placeholder="Brief description of this role"
              />
            </div>
          </div>

          {/* Permissions */}
          <div className="mb-3">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-medium text-gray-900">Permissions</h4>
              <button
                type="button"
                onClick={selectAll}
                className="text-xs text-primary-600 hover:text-primary-700 transition-colors font-medium"
              >
                {allSelected ? 'None' : 'All'}
              </button>
            </div>

            <div className="space-y-2 max-h-80 overflow-y-auto pr-2">
              {PERMISSION_GROUPS.map((group) => {
                const Icon = group.icon;
                const isExpanded = expandedSections[group.id];
                const groupKeys = group.permissions.map(p => p.key);
                const selectedInGroup = groupKeys.filter(k => selected.includes(k));
                const allInGroupSelected = groupKeys.every(k => selected.includes(k));

                return (
                  <div key={group.id} className="border border-gray-200 rounded-lg">
                    <div
                      className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => toggleSection(group.id)}
                    >
                      <div className="flex items-center space-x-2">
                        <div className="w-7 h-7 rounded-md bg-gray-100 flex items-center justify-center">
                          <Icon className="h-4 w-4 text-gray-500" />
                        </div>
                        <div>
                          <h5 className="text-sm font-medium text-gray-900">{group.title}</h5>
                          <p className="text-xs text-gray-500">{group.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-1">
                        <span className="text-xs text-gray-500">
                          {selectedInGroup.length}/{groupKeys.length}
                        </span>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); selectAllInSection(group); }}
                          className="text-xs text-primary-600 hover:text-primary-700 transition-colors font-medium"
                        >
                          {allInGroupSelected ? 'None' : 'All'}
                        </button>
                        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="border-t border-gray-200 p-3 bg-gray-50">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {group.permissions.map((permission) => (
                            <label
                              key={permission.key}
                              className="flex items-start space-x-2 p-2 bg-white rounded-lg border border-gray-200 hover:border-primary-300 transition-colors cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={selected.includes(permission.key)}
                                onChange={() => toggle(permission.key)}
                                className="mt-0.5 h-3 w-3 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="text-xs font-medium text-gray-900">{permission.label}</div>
                                <div className="text-xs text-gray-500 mt-0.5">{permission.description}</div>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-2 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-3 py-1.5 rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors"
            >
              {mode === 'create' ? 'Create Role' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
